import { useEffect, useState } from "react";
import { Mail, Globe, MapPin, Timer, Send } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { legacyApi } from "@/lib/legacyApi";

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  // Prefill from signed-in user (only for empty fields — never overwrite typed input).
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      email: f.email || user.email || "",
    }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      toast.error("That email address looks off.");
      return;
    }
    setSending(true);
    try {
      // The /api/contact endpoint (Resend-backed) still lives on the legacy
      // marketing backend — the mobile-app backend we now share for auth /
      // dashboards has no equivalent endpoint yet.
      await legacyApi.post("/contact", form);
      toast.success(
        "Message received. Tommy & Koboi will reply within 2 business days."
      );
      setForm({ ...form, subject: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Could not send your message. Please try again in a moment.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const infoRows = [
    { icon: Mail, label: "Email", value: "support@myadventure-passport.com" },
    { icon: Globe, label: "Website", value: "www.myadventure-passport.com" },
    { icon: MapPin, label: "Coverage", value: "Malaysia (expanding)" },
    { icon: Timer, label: "Response time", value: "Usually within 2 business days" },
  ];

  return (
    <>
      <section
        className="pt-[120px] md:pt-[140px] pb-10"
        style={{ background: "var(--surface-secondary)" }}
      >
        <div className="wrap text-center">
          <div className="eyebrow mb-4">Contact Us</div>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4" style={{ color: "var(--on-surface)" }}>
            We'd love to hear from you.
          </h1>
          <p className="body-lg max-w-2xl mx-auto">
            Drop us a note about anything — a bug, a campsite you love, a
            partnership idea. We read every message.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="wrap grid md:grid-cols-2 gap-8 items-start">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div
              className="rounded-[20px] p-6 flex items-center justify-center"
              style={{ background: "var(--card)", border: "1px solid var(--border-c)" }}
            >
              <img
                src="/assets/brand/logo.png"
                alt="MyAdventure-Passport"
                className="w-[180px] h-[180px] object-contain"
              />
            </div>
            <div
              className="rounded-[20px] p-6 md:p-8 text-white"
              style={{ background: "var(--surface-inverse)" }}
            >
              <div className="text-[12px] font-extrabold tracking-[0.18em] uppercase mb-5" style={{ color: "#FCD34D" }}>
                How to reach us
              </div>
              <ul className="space-y-4">
                {infoRows.map(({ icon: Icon, label, value }) => (
                  <li key={label} className="flex items-start gap-3">
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(252,211,77,0.16)", color: "#FCD34D" }}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold" style={{ color: "#FCD34D" }}>
                        {label}
                      </div>
                      <div className="text-sm text-white/90">{value}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={submit}
            noValidate
            className="rounded-[20px] p-6 md:p-8 space-y-4"
            style={{ background: "var(--card)", border: "1px solid var(--border-c)" }}
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: "var(--on-surface-muted)" }}>
                  Name
                </label>
                <input
                  data-testid="contact-name"
                  className="field"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Alex Camper"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: "var(--on-surface-muted)" }}>
                  Email
                </label>
                <input
                  data-testid="contact-email"
                  type="email"
                  className="field"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="alex@trailhead.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: "var(--on-surface-muted)" }}>
                Subject
              </label>
              <input
                data-testid="contact-subject"
                className="field"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="A campsite you love / partnership idea / bug report"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: "var(--on-surface-muted)" }}>
                Message
              </label>
              <textarea
                data-testid="contact-message"
                rows={6}
                className="field resize-none"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us anything…"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="btn btn-green w-full justify-center py-4"
            >
              <Send className="w-4 h-4" />
              {sending ? "Sending…" : "Send Message"}
            </button>
          </motion.form>
        </div>
      </section>
    </>
  );
}
